import { useEffect, useState } from "react";
import { bcfIssuesStore } from "../Stores/BCFIssuesStore";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { notificationError } from "utils/libHelpers/AntdNotification";
import { Dropdown, Row, Tag, Tooltip, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "Components/General/Button";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { AttachProjectFiles } from "Pages/Project/Documents/SharedAttachments/AttachProjectFiles";
import { keyBy, merge } from "lodash";
import { dayjs } from "Config/dayjs";
import { projectsDataStore } from "Stores/ProjectsStore";
import { bcfIssuesImportStore } from "../Stores/BCFIssuesImportStore";

const BCF_MODEL_FILE_TYPES = ["ifc", "ifczip", "stl", "las", "laz", "glb", "gltf", "ply", "cityjson"];

export interface BcfServerFileUnited {
  fileName?: string;
  hasXktVersion?: boolean;
  id?: number;
  modelReference?: string;
  relinkNeeded?: boolean;
  date?: string;
  ifc_project?: string;
  ifc_spatial_structure_element?: string;
  reference?: string;
  dirId?: number;
  fileStatus?: API_BCFIMPORTFILE_FILESTATUS;
  modelListId?: number;
}

interface BCFModelsProps extends WithModal {
  topicGuid: string;
  topicActions: { [action in API_BCFSERVERPROJECTEXTENSIONS_TOPIC_ACTIONS]?: boolean };
  projectActions: { [action in API_BCFSERVERPROJECTEXTENSIONS_PROJECT_ACTIONS]?: boolean };
  topicDeleted?: boolean;
  isInImportMode?: boolean;
  models?: API_BcfImportFile[];
  conflicts?: API_BcfImportRequestConflict[];
  setConfirmOnCancel?: (value: boolean) => void;
  registerModelSave?: (fn: (() => Promise<void>) | null) => void;
}

export const BCFModels = withModal((props: BCFModelsProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [models, setModels] = useState<BcfServerFileUnited[]>([]);
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState<API_BcfImportRequestConflict[]>();
  const [pendingRemovals, setPendingRemovals] = useState<BcfServerFileUnited[]>([]);

  useEffect(() => {
    if (!props.isInImportMode) {
      fetchModels();
    }
    if (props.models?.length) {
      setModels(props.models);
    }
    return () => {
      bcfIssuesImportStore.resetModelsForDelete();
    };
  }, []);

  useEffect(() => {
    setConflicts(props.conflicts);
  }, [props.conflicts]);

  useEffect(() => {
    if (props.isInImportMode) return;

    if (pendingRemovals.length > 0) {
      props.registerModelSave?.(async () => {
        await bcfIssuesStore.deleteModels(props.topicGuid, pendingRemovals);
      });
    } else {
      props.registerModelSave?.(null);
    }
  }, [pendingRemovals, props.isInImportMode, props.topicGuid, props.registerModelSave]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      const models = await bcfIssuesStore.fetchModels(props.topicGuid);
      await bcfIssuesStore.fetchFilesOfTopic(props.topicGuid);
      const topicFiles = keyBy(bcfIssuesStore.filesOfTopics, "modelReference");
      const unitedModels = models
        .map((item) => merge(item, topicFiles[item.reference]))
        .map((item) => {
          item.fileName = item.fileName || item.filename;
          delete item.filename;
          return item as BcfServerFileUnited;
        });
      setModels(unitedModels);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = async (e, record: BcfServerFileUnited) => {
    e.domEvent.preventDefault();
    e.domEvent.stopPropagation();

    switch (e.key) {
      case "remove":
        try {
          if (props.isInImportMode) {
            bcfIssuesImportStore.setModelsForDelete(props.topicGuid, record.modelListId);
            const filteredModels = models.filter((item) => item.modelListId !== record.modelListId);
            setModels(filteredModels);
            setConflicts(bcfIssuesImportStore.findDuplicatesAndUpdate(filteredModels, props.topicGuid));
          } else {
            setPendingRemovals((prev) => [...prev, record]);
            setModels((prev) => prev.filter((item) => item !== record));
          }
          props.setConfirmOnCancel?.(true);
          message.success(t("Model removed"));
        } catch (error) {
          throw error;
        }
        break;
      case "relink":
        showRelink(record);
        break;
      case "redirect":
        window.open(
          `${window.location.origin}/${window.GLOBAL.API_BASE}/project/${projectsDataStore.projectId}/documents/docs/${record.dirId}?file=${record.id}`
        );
        break;
      default:
        notificationError("Error", "Unknown action" + e.key);
        break;
    }
  };

  const singleFileActions = (record: BcfServerFileUnited) => {
    const items = [];
    if (props.projectActions.createDocument) {
      items.push({
        label: t("Remove"),
        key: "remove",
        disabled: props.topicDeleted,
        icon: <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} className="mr-5" />,
      });
    }
    if (!props.isInImportMode && props.projectActions.createDocument) {
      items.push({
        label: t("Relink"),
        key: "relink",
        disabled: props.isInImportMode || props.topicDeleted,
        icon: <FontAwesomeIcon icon={["far", "link"]} fixedWidth={true} className="mr-5" />,
      });
    }
    // items.push({
    //   label: t("Jump to directory"),
    //   key: "redirect",
    //   disabled: false,
    //   icon: <FontAwesomeIcon icon={["far", "folder"]} fixedWidth={true} className="mr-5" />,
    // });

    return (
      <Dropdown
        styles={{ root: { zIndex: 1000000 } }}
        menu={{ items, onClick: (e) => handleMenuClick(e, record) }}
        trigger={["click"]}
        forceRender
      >
        <div className="docs-dropdown-trigger pointer" onClick={(e) => e.stopPropagation()}>
          <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
        </div>
      </Dropdown>
    );
  };
  const renderIcons = (rowdata: BcfServerFileUnited) => {
    if (rowdata.relinkNeeded || rowdata.relinkNeeded === undefined) {
      return (
        <FontAwesomeIcon
          className="email-action-icon"
          style={{ width: 25, height: 25, color: "red" }}
          icon={["far", "minus-circle"]}
          fixedWidth={true}
        />
      );
    } else {
      return (
        <FontAwesomeIcon
          className="email-action-icon"
          style={{ width: 25, height: 25, color: "green" }}
          icon={["far", "check-circle"]}
          fixedWidth={true}
        />
      );
    }
  };

  const statusDisplay = (record: BcfServerFileUnited) => {
    const tenderCallStatusMap: {
      [key in API_BCFIMPORTFILE_FILESTATUS]: { message: string; color: string; hint: string };
    } = {
      NEW: {
        message: "New",
        color: "blue",
        hint: "A new relation is found in the import that did not previously exist in the issue.",
      },
      RETAINED: {
        message: "Retained",
        color: "yellow",
        hint: "This relation is not included in the import but will be kept from the existing issue.",
      },
      UNCHANGED: {
        message: "Unchanged",
        color: "green",
        hint: "This relation already exists in the issue and remains as is.",
      },
    };
    return (
      <>
        <Tooltip title={t(tenderCallStatusMap[record.fileStatus].hint)}>
          <Tag
            color={tenderCallStatusMap[record.fileStatus].color}
            style={{ fontSize: 16 }}
            onClick={(e) => e.preventDefault()}
          >
            {t(tenderCallStatusMap[record.fileStatus].message)}
          </Tag>
        </Tooltip>
      </>
    );
  };

  const conflictDisplay = (record: BcfServerFileUnited) => {
    const conflict = conflicts?.find((item) => item.modelListId === record.modelListId);
    return (
      <Row justify={"center"}>
        {conflict ? (
          <Tooltip title={conflict.error?.includes('"null"') ? t("Missing value") : conflict.error}>
            <FontAwesomeIcon
              fixedWidth={true}
              size="lg"
              style={{ color: conflict.optional ? "#faad14" : "red" }}
              icon={["far", "exclamation-triangle"]}
            />
          </Tooltip>
        ) : (
          <FontAwesomeIcon fixedWidth={true} size="lg" style={{ color: "green" }} icon={["far", "check-circle"]} />
        )}
      </Row>
    );
  };

  const columns = () => {
    const tableColumns: TableColumnProps<BcfServerFileUnited>[] = [
      {
        dataKey: "fileName",
        label: t("Filename"),
        flexGrow: 1,
        width: 450,
        cellRenderer: ({ rowData }) => rowData.filename || rowData.fileName,
      },
      {
        dataKey: "date",
        label: t("Date"),
        flexGrow: 1,
        width: 140,
        cellRenderer: ({ rowData }) => (rowData.date ? dayjs(rowData.date).format("lll") : null),
      },
      {
        dataKey: "id",
        label: t("Actions"),
        width: 70,
        cellRenderer: ({ rowData }) => singleFileActions(rowData),
      },
    ];
    if (!props.isInImportMode) {
      tableColumns.splice(tableColumns.length - 1, 0, {
        dataKey: "relinkNeeded",
        label: t("Link"),
        flexGrow: 1,
        width: 30,
        cellRenderer: ({ rowData }) => renderIcons(rowData),
      });
    }
    if (props.isInImportMode) {
      tableColumns.splice(tableColumns.length - 1, 0, {
        dataKey: "ifc_project",
        label: t("ifcProject"),
        flexGrow: 1,
        width: 150,
      });
    }
    if (props.isInImportMode) {
      tableColumns.splice(tableColumns.length - 1, 0, {
        dataKey: "fileStatus",
        label: t("Status"),
        width: 110,
        cellRenderer: ({ rowData }) => statusDisplay(rowData),
      });
    }
    if (props.isInImportMode) {
      tableColumns.splice(tableColumns.length - 1, 0, {
        dataKey: "id",
        label: t("Conflict"),
        width: 90,
        cellRenderer: ({ rowData }) => conflictDisplay(rowData),
      });
    }
    return tableColumns;
  };

  const addFiles = async (files: API_FileAttachment[]) => {
    try {
      const linkedCount = await bcfIssuesStore.addModels(props.topicGuid, files);
      await fetchModels();
      if (linkedCount > 0) {
        message.success(t("Model linked"));
      } else {
        message.error(t("File type not allowed"));
      }
    } catch (error) {
      throw error;
    }
  };

  const relink = async (files: API_FileAttachment[], targetFile: BcfServerFileUnited) => {
    try {
      const relinked = await bcfIssuesStore.relinkModel(props.topicGuid, targetFile, files[0].id);
      await fetchModels();
      if (relinked) {
        message.success(t("Model relinked"));
      } else {
        message.error(t("File type not allowed"));
      }
    } catch (error) {
      throw error;
    }
  };

  const showAddFile = () => {
    props.showModal(AttachProjectFiles, {
      modalProps: {
        title: t("Select models from project"),
        width: 800,
        footer: null,
        zIndex: 100020,
      },
      componentProps: { addFiles: addFiles, allowedFileTypes: BCF_MODEL_FILE_TYPES },
    });
  };

  const showRelink = (record) => {
    props.showModal(AttachProjectFiles, {
      modalProps: {
        title: t("Relink model"),
        width: 800,
        footer: null,
        zIndex: 1020000,
      },
      componentProps: {
        addFiles: (files) => relink(files, record),
        allowedFileTypes: BCF_MODEL_FILE_TYPES,
        isRadioSelection: true,
      },
    });
  };

  return (
    <>
      <Row justify={"end"} style={{ marginBottom: 5 }}>
        {!props.isInImportMode && props.projectActions.createDocument && (
          <Button
            type="primary"
            faIcon="plus"
            onClick={showAddFile}
            disabled={props.isInImportMode || props.topicDeleted}
          >
            {t("Link models")}
          </Button>
        )}
      </Row>
      <ScrollableTable
        rowKey="id"
        wrapperStyles={{ height: null, flex: "1 1 auto", minHeight: 270 }}
        data={models}
        columns={columns()}
        loading={loading}
        resizeableHeaders={"BCF_MODELS"}
        ignoreResizeableHeaders={["id"]}
      />
    </>
  );
});
